import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { AccountUsageResponse } from "@/lib/api/routes/account-usage";
import { cn } from "@/lib/utils";
import { Check, Crown, Loader2, Mail, X, Zap } from "lucide-react";
import * as React from "react";

/* ------------------------------------------------------------------ */
/*  Types & constants                                                  */
/* ------------------------------------------------------------------ */

type BillingPeriod = "monthly" | "annual";

type Tier = {
  id: string;
  name: string;
  monthlyPrice: number | null; // null = contact sales
  annualPrice: number | null;
  features: string[];
  limits: {
    quotes: string;
    shipments: string;
    apiCalls: string;
    carriers: string;
    templates: string;
    teamMembers: string;
  };
  cta: string;
  highlighted: boolean;
};

const TIERS: Tier[] = [
  {
    id: "trial",
    name: "Trial",
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      "Basic quoting & booking",
      "Real-time tracking",
      "Email support",
    ],
    limits: {
      quotes: "50/mo",
      shipments: "10/mo",
      apiCalls: "100/day",
      carriers: "3",
      templates: "5",
      teamMembers: "1",
    },
    cta: "Current plan",
    highlighted: false,
  },
  {
    id: "small",
    name: "Basic",
    monthlyPrice: 150,
    annualPrice: 1500,
    features: [
      "Everything in Trial",
      "Unlimited quoting",
      "Address book",
      "Priority email support",
    ],
    limits: {
      quotes: "200/mo",
      shipments: "50/mo",
      apiCalls: "500/day",
      carriers: "10",
      templates: "20",
      teamMembers: "3",
    },
    cta: "Upgrade to Basic",
    highlighted: false,
  },
  {
    id: "unlimited",
    name: "Premium",
    monthlyPrice: 750,
    annualPrice: 7500,
    features: [
      "Everything in Basic",
      "Saved templates",
      "Custom reports",
      "AI rate recommendations",
      "Team collaboration",
      "Labels & BOL",
    ],
    limits: {
      quotes: "Unlimited",
      shipments: "Unlimited",
      apiCalls: "5,000/day",
      carriers: "Unlimited",
      templates: "Unlimited",
      teamMembers: "10",
    },
    cta: "Upgrade to Premium",
    highlighted: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    monthlyPrice: null,
    annualPrice: null,
    features: [
      "Everything in Premium",
      "Unlimited everything",
      "Dedicated account manager",
      "Custom integrations",
      "SLA guarantee",
      "SSO / SAML",
    ],
    limits: {
      quotes: "Unlimited",
      shipments: "Unlimited",
      apiCalls: "Unlimited",
      carriers: "Unlimited",
      templates: "Unlimited",
      teamMembers: "Unlimited",
    },
    cta: "Contact Sales",
    highlighted: false,
  },
];

const LIMIT_ROWS: { key: keyof Tier["limits"]; label: string }[] = [
  { key: "quotes", label: "Quotes" },
  { key: "shipments", label: "Shipments" },
  { key: "apiCalls", label: "API calls" },
  { key: "carriers", label: "Carriers" },
  { key: "templates", label: "Templates" },
  { key: "teamMembers", label: "Team members" },
];

/**
 * Maps legacy Stripe tier IDs to their Polar equivalents for display.
 * Existing users with "starter" should see themselves on the "Basic" tier, etc.
 */
function normalizeTierId(tierId: string): string {
  if (tierId === "starter") return "small";
  if (tierId === "professional") return "unlimited";
  return tierId;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatPrice(price: number | null, period: BillingPeriod): string {
  if (price === null) return "Custom";
  if (price === 0) return "Free";
  if (period === "annual") return `$${price}/yr`;
  return `$${price}/mo`;
}

function freeMonthsWithAnnual(monthly: number | null, annual: number | null): number | null {
  if (monthly === null || annual === null || monthly === 0) return null;
  const monthsIncluded = annual / monthly;
  const freeMonths = 12 - monthsIncluded;
  return freeMonths > 0 ? Math.round(freeMonths) : null;
}

/** Determine which tier to recommend based on usage. */
function recommendedTier(data: AccountUsageResponse | null): string {
  if (!data) return "unlimited";
  const { usage } = data;
  const quotesUsed = usage.quotes.used;
  const shipmentsUsed = usage.shipments.used;

  // If heavily using, recommend unlimited
  if (quotesUsed > 200 || shipmentsUsed > 50) return "unlimited";
  // Moderate usage -> small
  if (quotesUsed > 20 || shipmentsUsed > 5) return "small";
  // Light usage still gets unlimited as best value
  return "unlimited";
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function UpgradeModal() {
  const [open, setOpen] = React.useState(false);
  const [billingPeriod, setBillingPeriod] = React.useState<BillingPeriod>("annual");
  const [loadingTier, setLoadingTier] = React.useState<string | null>(null);
  const [usageData, setUsageData] = React.useState<AccountUsageResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const backdropRef = React.useRef<HTMLDivElement>(null);

  // Listen for the custom event dispatched by TrialBanner or other triggers
  React.useEffect(() => {
    function handleOpen() {
      setOpen(true);
      setError(null);
    }

    window.addEventListener("shippeek:open-upgrade-modal", handleOpen);
    return () => window.removeEventListener("shippeek:open-upgrade-modal", handleOpen);
  }, []);

  // Fetch usage data when modal opens
  React.useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function fetchUsage() {
      try {
        const res = await fetch("/api/account/usage", { credentials: "include" });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && json.status === "success") {
          setUsageData(json.data);
        }
      } catch {
        // Non-critical: usage data is optional for the modal
      }
    }

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Escape key handler
  React.useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !loadingTier) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, loadingTier]);

  // Lock body scroll when open
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const closeModal = React.useCallback(() => {
    if (loadingTier) return;
    setOpen(false);
  }, [loadingTier]);

  const handleBackdropMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) closeModal();
    },
    [closeModal],
  );

  const handleUpgrade = React.useCallback(
    async (tierId: string) => {
      if (tierId === "trial") return;

      if (tierId === "enterprise") {
        window.location.href = "mailto:sales@shippeek.com?subject=Enterprise%20Plan%20Inquiry";
        return;
      }

      setLoadingTier(tierId);
      setError(null);

      try {
        const res = await fetch("/api/account/billing/checkout", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tier: tierId, billing_period: billingPeriod }),
        });

        const json = await res.json();

        if (!res.ok) {
          setError(json.error || "Failed to create checkout session.");
          return;
        }

        if (json.data?.checkout_url) {
          window.location.href = json.data.checkout_url;
        } else {
          setError("No checkout URL returned. Please try again.");
        }
      } catch {
        setError("Network error. Please check your connection and try again.");
      } finally {
        setLoadingTier(null);
      }
    },
    [billingPeriod],
  );

  if (!open) return null;

  const recommended = recommendedTier(usageData);
  const currentTier = normalizeTierId(usageData?.subscription_tier ?? "trial");

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-8 sm:items-center sm:pt-4"
      onMouseDown={handleBackdropMouseDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <div
        className="relative w-full max-w-5xl rounded-xl border bg-background shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 id="upgrade-modal-title" className="text-lg font-semibold">
              Choose your plan
            </h2>
            <p className="text-sm text-muted-foreground">
              Unlock more features and higher limits
            </p>
          </div>
          <button
            type="button"
            aria-label="Close upgrade modal"
            className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={closeModal}
            disabled={!!loadingTier}
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 border-b px-6 py-3">
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              billingPeriod === "monthly"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setBillingPeriod("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              billingPeriod === "annual"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => setBillingPeriod("annual")}
          >
            Annual
          </button>
          {billingPeriod === "annual" && (
            <Badge className="border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              2 months free
            </Badge>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="border-b px-6 py-2">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Pricing tiers grid */}
        <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((tier) => {
            const isRecommended = tier.id === recommended && currentTier !== tier.id;
            const isCurrent = currentTier === tier.id;
            const freeMonths = freeMonthsWithAnnual(tier.monthlyPrice, tier.annualPrice);
            const isLoading = loadingTier === tier.id;
            const isDisabled = !!loadingTier;
            const price =
              billingPeriod === "annual" ? tier.annualPrice : tier.monthlyPrice;

            return (
              <div
                key={tier.id}
                className={cn(
                  "relative flex flex-col rounded-lg border p-5",
                  tier.highlighted && "border-primary shadow-md",
                  isRecommended && "ring-2 ring-primary",
                )}
              >
                {/* Recommended badge */}
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      <Crown className="size-3" />
                      Recommended
                    </Badge>
                  </div>
                )}

                {/* Tier name */}
                <div className="mb-3">
                  <h3 className="text-base font-semibold">{tier.name}</h3>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold tracking-tight">
                      {price === null
                        ? "Custom"
                        : price === 0
                          ? "Free"
                          : billingPeriod === "annual"
                            ? `$${Math.round(price / 12)}`
                            : `$${price}`}
                    </span>
                    {price !== null && price > 0 && (
                      <span className="text-sm text-muted-foreground">/mo</span>
                    )}
                  </div>
                  {billingPeriod === "annual" && price !== null && price > 0 && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatPrice(price, "annual")} billed annually
                    </p>
                  )}
                  {billingPeriod === "annual" && freeMonths !== null && (
                    <p className="mt-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {freeMonths} months free
                    </p>
                  )}
                </div>

                {/* CTA button */}
                <div className="mb-4">
                  {isCurrent ? (
                    <Button variant="outline" className="w-full" disabled>
                      Current plan
                    </Button>
                  ) : tier.id === "enterprise" ? (
                    <Button
                      variant={tier.highlighted ? "default" : "outline"}
                      className="w-full"
                      disabled={isDisabled}
                      onClick={() => handleUpgrade(tier.id)}
                    >
                      <Mail className="size-4" />
                      Contact Sales
                    </Button>
                  ) : tier.id === "trial" ? (
                    <Button variant="outline" className="w-full" disabled>
                      Free tier
                    </Button>
                  ) : (
                    <Button
                      variant={tier.highlighted || isRecommended ? "default" : "outline"}
                      className="w-full"
                      disabled={isDisabled}
                      onClick={() => handleUpgrade(tier.id)}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="size-4" />
                          {tier.cta}
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Features list */}
                <ul className="mb-4 flex-1 space-y-2">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Limits breakdown */}
                <div className="border-t pt-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Limits
                  </p>
                  <dl className="space-y-1">
                    {LIMIT_ROWS.map((row) => (
                      <div key={row.key} className="flex items-center justify-between text-xs">
                        <dt className="text-muted-foreground">{row.label}</dt>
                        <dd className="font-medium">{tier.limits[row.key]}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 text-center text-xs text-muted-foreground">
          All plans include SSL encryption, real-time tracking, and 99.9% uptime SLA.
          Need help choosing?{" "}
          <a
            href="mailto:support@shippeek.com"
            className="text-primary underline-offset-2 hover:underline"
          >
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}
